import { Link } from 'react-router-dom';
import { Zap, Clock, AlertTriangle, TrendingUp, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusDot, PriorityBadge, StageBadge } from '@/components/StatusBadge';
import { timeAgo } from '@/data/mockData';
import { useDealershipData } from '@/data/hooks/useDealershipData';
import { useAuth } from '@/contexts/AuthContext';
import { NEXT_STEP_LABELS } from '@/types';
import { Logo } from '@/components/Logo';
import { MobileNav } from '@/components/MobileNav';
import { NewDealDialog } from '@/components/NewDealDialog';
import { QuickCaptureSheet } from '@/components/QuickCaptureSheet';
import { OnboardingChecklist } from '@/components/OnboardingChecklist';
import { MyWishlistCard } from '@/components/wishlist/MyWishlistCard';
import { MyWeekCard } from '@/components/MyWeekCard';
import { ModeBadge } from '@/components/ModeBadge';
import { HouseRulesCard } from '@/components/HouseRulesCard';
import { PaceStrip } from '@/components/rep/PaceStrip';
import { NextBestActionCard } from '@/components/rep/NextBestActionCard';
import { MomentumBadge } from '@/components/rep/MomentumBadge';
import { StreakChips } from '@/components/rep/StreakChips';
import { sortByPriority, REASON_LABEL } from '@/lib/dealPriority';
import { useDealershipSettings } from '@/data/hooks/useDealershipSettings';

export default function RepDashboard() {
  const { mode, userId, userName, dealershipName } = useAuth();
  const { deals } = useDealershipData();
  const { settings } = useDealershipSettings();
  // In demo mode the mock rep id is 'rep-alex'; in real mode it's the auth user id.
  const repId = mode === 'demo' ? 'rep-alex' : userId;
  const myDeals = deals.filter(d => d.rep_id === repId);
  const needFollowUp = myDeals.filter(d => d.status === 'slowing' || d.status === 'no_activity');
  const activeDeals = myDeals.filter(d => d.stage !== 'sold');
  const sortedActive = sortByPriority(activeDeals, settings.stale_deal_hours || 72);
  const hotCount = myDeals.filter(d => d.priority === 'hot').length;
  // Simple derived score until a real scoring service exists
  const score = Math.min(
    100,
    Math.round(activeDeals.length * 8 + hotCount * 6 + (myDeals.length === 0 ? 0 : 30)),
  );
  const initials = (userName || 'You').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const firstName = (userName || 'You').split(' ')[0];

  const todayActions = [
    { text: `${needFollowUp.length} deals need follow-up`, urgent: needFollowUp.length > 0 },
    { text: `${myDeals.filter(d => !d.next_step && d.stage !== 'sold').length} deals missing next step`, urgent: myDeals.some(d => !d.next_step && d.stage !== 'sold') },
    { text: `${hotCount} hot deals in your pipeline`, urgent: hotCount > 0 },
    { text: `${activeDeals.length} active deals total`, urgent: false },
  ];

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-0">
      {/* Mobile Header */}
      <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-sm border-b border-border px-4 py-3 lg:hidden">
        <div className="flex items-center justify-between">
          <Link to="/" aria-label="PullOver.App home" className="flex items-center gap-2">
            <Logo variant="square" className="h-9 w-9 object-contain" />
            <span className="text-xs text-muted-foreground">{dealershipName || 'My Dealership'}</span>
          </Link>
          <div className="flex items-center gap-2">
            <ModeBadge />
            <span className="text-sm font-medium">{firstName}</span>
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold">{initials}</div>
          </div>
        </div>
      </header>

      <div className="p-4 space-y-4 max-w-2xl mx-auto">
        <OnboardingChecklist />
        <HouseRulesCard />
        <MyWeekCard />
        {/* Score + Quick Action */}
        <div className="flex gap-3">
          <Card className="flex-1">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">My Score</p>
              <p className="text-4xl font-bold text-primary">{score}</p>
              <p className="text-xs text-muted-foreground mb-2">/100 today</p>
              <MomentumBadge />
            </CardContent>
          </Card>
          <Card className="flex-1">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Active Deals</p>
              <p className="text-4xl font-bold">{activeDeals.length}</p>
              <p className="text-xs text-muted-foreground">{myDeals.filter(d => d.priority === 'hot').length} hot</p>
            </CardContent>
          </Card>
        </div>
        <StreakChips />
        <PaceStrip />
        <NextBestActionCard />

        <QuickCaptureSheet />
        <div className="grid grid-cols-2 gap-3">
          <Link to="/log-activity">
            <Button size="lg" variant="outline" className="w-full text-base h-14 gap-2">
              <Zap className="h-5 w-5" /> Log Activity
            </Button>
          </Link>
          <NewDealDialog />
        </div>

        {/* Today's Actions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" /> Today's Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {todayActions.map((action, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-lg ${action.urgent ? 'bg-primary/5 border border-primary/10' : 'bg-muted/30'}`}>
                  {action.urgent ? <AlertTriangle className="h-4 w-4 text-primary shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                  <span className="text-sm">{action.text}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* My Deals */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">My Deals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {myDeals.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No deals assigned to you yet. New leads will appear here.
                </p>
              )}
              {sortedActive.map(deal => (
                <Link key={deal.id} to={`/deals/${deal.id}`} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{deal.customer_name}</p>
                      <PriorityBadge priority={deal.priority} />
                      {deal._reason !== 'normal' && (
                        <span className="text-[10px] uppercase tracking-wide text-primary/80 font-semibold">
                          {REASON_LABEL[deal._reason]}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{deal.vehicle}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <StageBadge stage={deal.stage} />
                      {deal.next_step ? (
                        <span className="text-xs text-muted-foreground">Next: {NEXT_STEP_LABELS[deal.next_step]}</span>
                      ) : (
                        <span className="text-xs text-red-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> No next step</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className="text-sm font-medium">${(deal.estimated_value / 1000).toFixed(0)}K</p>
                    <p className="text-xs text-muted-foreground">{timeAgo(deal.last_activity_at)}</p>
                    <StatusDot status={deal.status} />
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* My Wishlist */}
        <MyWishlistCard />

        {/* Follow-Up Needed */}
        {needFollowUp.length > 0 && (
          <Card className="border-yellow-500/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-400" /> Needs Follow-Up
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {needFollowUp.map(deal => (
                  <Link key={deal.id} to={`/deals/${deal.id}`} className="flex items-center justify-between p-3 rounded-lg bg-yellow-500/5 hover:bg-yellow-500/10 transition-colors">
                    <div>
                      <p className="font-medium text-sm">{deal.customer_name}</p>
                      <p className="text-xs text-muted-foreground">{deal.vehicle}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-yellow-400">Last: {timeAgo(deal.last_activity_at)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <MobileNav />
    </div>
  );
}
